export type DocPage = {
  slug: string;
  path: string;
  title: string;
  description: string;
  end?: boolean;
};

export type DocSection = {
  label: string;
  pages: DocPage[];
};

export const docSections: DocSection[] = [
  {
    label: "Getting Started",
    pages: [
      {
        slug: "introduction",
        path: "/docs",
        title: "Introduction",
        description: "What CloudQuote is, what's in the platform, and where to go next.",
        end: true,
      },
      {
        slug: "quickstart",
        path: "/docs/quickstart",
        title: "Quick Start",
        description: "Sign up, get an API key, and make your first call in under five minutes.",
      },
      {
        slug: "authentication",
        path: "/docs/authentication",
        title: "Authentication & API Keys",
        description: "All credential types: domain whitelists, public keys, private keys, OAuth, and IP ranges.",
      },
    ],
  },
  {
    label: "Core APIs",
    pages: [
      {
        slug: "rest-api",
        path: "/docs/rest-api",
        title: "REST API Conventions",
        description: "Request structure, response formats (JSON / CSV / XML), pagination, and error handling.",
      },
      {
        slug: "newsrpm-api",
        path: "/docs/newsrpm-api",
        title: "NewsRPM News API",
        description: "Search articles, render bodies, fetch headlines by slug, and look up by ticker.",
      },
      {
        slug: "search-api",
        path: "/docs/search-api",
        title: "Search API (Vector & Full-Text)",
        description: "Run hybrid keyword + semantic queries across equities, news, fundamentals, and events.",
      },
    ],
  },
  {
    label: "AI & Automation",
    pages: [
      {
        slug: "chat-api",
        path: "/docs/chat-api",
        title: "Chat API (OpenAI-Compatible)",
        description: "A conversational, function-calling interface to every CloudQuote endpoint.",
      },
      {
        slug: "mcp",
        path: "/docs/mcp",
        title: "Model Context Protocol (MCP)",
        description: "A model-agnostic JSON tool protocol so any LLM can call CloudQuote APIs.",
      },
      {
        slug: "agents",
        path: "/docs/agents",
        title: "Building AI Agents",
        description: "Patterns for autonomous research, monitoring, and trading agents.",
      },
    ],
  },
  {
    label: "Integration Guides",
    pages: [
      {
        slug: "frontend-integration",
        path: "/docs/frontend-integration",
        title: "Front-End Integration",
        description: "Calling CloudQuote from browsers, mobile apps, and single-page apps using CORS.",
      },
      {
        slug: "backend-integration",
        path: "/docs/backend-integration",
        title: "Back-End Integration",
        description: "Server-to-server requests, private keys, and IP-whitelisted enterprise patterns.",
      },
      {
        slug: "rss-feeds",
        path: "/docs/rss-feeds",
        title: "Publishing News via RSS",
        description: "The NewsRPM RSS 2.0 ingestion specification with examples and ticker tagging.",
      },
    ],
  },
  {
    label: "Data Coverage",
    pages: [
      {
        slug: "data-coverage-equities",
        path: "/docs/data-coverage",
        title: "Equities",
        description: "Equity exchange coverage — historical, delayed, real-time, and Level 2 data.",
        end: true,
      },
      {
        slug: "data-coverage-crypto",
        path: "/docs/data-coverage/crypto",
        title: "Crypto & Forex",
        description: "Cryptocurrency exchanges, FX rates, and spot metals coverage.",
      },
      {
        slug: "data-coverage-indices",
        path: "/docs/data-coverage/indices",
        title: "Indices",
        description: "Major index families — Dow Jones, S&P, CBOE, Nasdaq, TSX, Euronext.",
      },
      {
        slug: "data-coverage-funds",
        path: "/docs/data-coverage/funds",
        title: "Funds",
        description: "Mutual fund coverage for US and Canadian markets.",
      },
      {
        slug: "data-coverage-commodities",
        path: "/docs/data-coverage/commodities",
        title: "Commodities & Futures",
        description: "Global futures and commodities exchange coverage.",
      },
    ],
  },
];

export const allDocPages: DocPage[] = docSections.flatMap(s => s.pages);

export function getNeighbors(slug: string): { prev?: DocPage; next?: DocPage } {
  const idx = allDocPages.findIndex(p => p.slug === slug);
  if (idx < 0) return {};
  return {
    prev: idx > 0 ? allDocPages[idx - 1] : undefined,
    next: idx < allDocPages.length - 1 ? allDocPages[idx + 1] : undefined,
  };
}
